import { useCallback, useMemo } from 'react'

import {
  PowerType,
  VoltageClass,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import {
  type BusinessMasterRow,
  convertListBusinessMastersResponse,
  useListBusinessMasters,
} from '~/hooks/businessMaster/useListBusinessMasters'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import { plantPowerTypeLabel } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'
import type { PlantMasterFormInput } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/schema'
import { findSystemCodeAreaMismatch } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/systemCodeAreaMismatch'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { FieldWarningText } from '~/pages/AdminOcctoMaster/tabs/_common/components/FieldWarningText'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { OwnerMembersSection } from '~/pages/AdminOcctoMaster/tabs/_common/components/OwnerMembersSection'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import {
  resolveAsOfDate,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'
import { voltageClassLabel } from '~/pages/AdminOcctoMaster/tabs/_common/models/voltageClass'

const POWER_TYPE_OPTIONS: PowerType[] = [
  PowerType.NON_ADJUSTABLE,
  PowerType.ADJUSTABLE,
  PowerType.FIT_EXCEPTION_1,
  PowerType.FIT_EXCEPTION_2,
  PowerType.FIT_EXCEPTION_3,
  PowerType.FIP,
]

const VOLTAGE_CLASS_OPTIONS: VoltageClass[] = [
  VoltageClass.EXTRA_HIGH,
  VoltageClass.HIGH,
  VoltageClass.LOW,
]

type Props = {
  control: Control<PlantMasterFormInput>
  errors: FieldErrors<PlantMasterFormInput>
  /**
   * 代表事業者 (businessMasterId) を合同所有者の先頭 (members[0]) に同期するため
   * OwnerMembersSection に渡す。
   */
  setValue: UseFormSetValue<PlantMasterFormInput>
  /**
   * true の場合、 plantCode を readonly で表示する (edit 時)。
   * AC-006 準拠: 系統コードは登録後に変更不可。エリアは系統コードから決まらず、
   * 登録後も選び直せる (変更は backend が歯止めを掛ける)。
   */
  isEdit?: boolean
  disabled?: boolean
}

/**
 * 発電所マスタの Create / Edit 共通 form fields コンポーネント。
 *
 * isEdit={true} のとき plantCode を readonly にする (AC-006)。
 * 合同所有者 (members) の編集 UI と代表同期は BgMaster と共通の
 * OwnerMembersSection に委譲する (= 代表はフラグでなく businessMasterId の
 * 一致で表現する)。
 */
export const PlantMasterFormFields = ({
  control,
  errors,
  setValue,
  isEdit = false,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data: businessMastersData } = useListBusinessMasters()
  // form の毎 render で再評価されないよう memo 化する (= 入力のたびに走る hot path)。
  const businessMasters: BusinessMasterRow[] = useMemo(
    () =>
      businessMastersData !== undefined
        ? convertListBusinessMastersResponse(businessMastersData)
        : [],
    [businessMastersData],
  )

  // select の選択肢 (DS `Select` は `{ value: string, label }` を取る)。
  // ラベルは i18next 依存 (= 言語切替で変わる) なので render ごとに組み立てる
  // (native 時代に JSX 内で `.map` していたのと同じ扱い)。
  const areaCodeOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))
  const powerTypeOptions = POWER_TYPE_OPTIONS.map((pt) => ({
    value: String(pt),
    label: plantPowerTypeLabel(pt),
  }))
  const voltageClassOptions = VOLTAGE_CLASS_OPTIONS.map((vc) => ({
    value: String(vc),
    label: voltageClassLabel(vc),
  }))
  // AC-MOI-023: 代表事業者・合同所有者の候補は、 発電所の適用開始日時点で有効な事業者に絞る。
  const today = useTodayJst()
  const plantCode = useWatch({ control, name: 'plantCode' })
  const areaCode = useWatch({ control, name: 'areaCode' })
  const systemCodeAreaMismatch = findSystemCodeAreaMismatch(plantCode, areaCode)
  const validFrom = useWatch({ control, name: 'validFrom' })
  const targetDate = resolveAsOfDate(undefined, validFrom, today)
  const asOfFilteredBusinessMasters = useMemo(
    () =>
      businessMasters.filter((bm) =>
        isActiveOn(bm.validFrom, bm.validTo, targetDate),
      ),
    [businessMasters, targetDate],
  )
  const businessMasterOptions = useMemo(
    () =>
      asOfFilteredBusinessMasters.map((bm) => ({
        value: bm.id,
        label: bm.businessName,
      })),
    [asOfFilteredBusinessMasters],
  )
  // 候補外 (as-of narrow 後) の既存参照 fallback ラベル。対象日時点で終了して
  // いれば「（終了）」サフィックス付き、 物理削除済みなら「(削除済み)」を返す
  // (AC-MOI-023、occto-master-fk-as-of-filter 設計書 Decision 2)。 list
  // クエリ取得中は unfiltered に undefined を渡し、 実在参照が一瞬
  // 「(削除済み)」に誤表示されるのを防ぐ (PR #5920 レビュー指摘)。
  const businessMasterFallbackLabel = useCallback(
    (id: string, asOf: CivilDate) =>
      resolveExpiryFallbackLabel(
        businessMastersData === undefined ? undefined : businessMasters,
        id,
        asOf,
        {
          idOf: (bm) => bm.id,
          labelOf: (bm) => bm.businessName,
          validTo: (bm) => bm.validTo,
          deletedLabel: t(
            'page:adminOcctoMaster.common.masterFallback.deleted',
          ),
          expiredSuffix: t(
            'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
          ),
          future: {
            validFrom: (bm) => bm.validFrom,
            suffix: t(
              'page:adminOcctoMaster.common.masterFallback.futureSuffix',
            ),
          },
        },
      ),
    [businessMasters, businessMastersData, t],
  )

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix="plantmaster"
        />
      )}

      {/* plantCode + areaCode: 2カラム (design 順: 系統コード / エリア) */}
      <SimpleGrid gap={3} columns={2}>
        {/* plantCode */}
        <Controller
          control={control}
          name="plantCode"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.plantCode !== undefined}
              required
            >
              <FieldLabel htmlFor="plantmaster-plantCode">
                {t('page:adminOcctoMaster.plantMaster.form.plantCode')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <ImmutableInput
                {...field}
                id="plantmaster-plantCode"
                maxLength={5}
                placeholder={t(
                  'page:adminOcctoMaster.plantMaster.form.plantCodePlaceholder',
                )}
                readOnly={isEdit}
              />
              {errors.plantCode !== undefined && (
                <FieldErrorText>{errors.plantCode.message}</FieldErrorText>
              )}
              {/* 系統コードの 1 桁目とエリアコードが違う時の注意 (DE 判断 2026-10-29)。
                  保存は止めない (form の検証エラーにしない)。 */}
              {systemCodeAreaMismatch !== undefined &&
                areaCode !== undefined && (
                  <FieldWarningText>
                    {t(
                      'page:adminOcctoMaster.plantMaster.form.systemCodeAreaMismatch',
                      {
                        digit: systemCodeAreaMismatch.digit,
                        areaName: convertAreaLabel(areaCode),
                        areaCode: systemCodeAreaMismatch.areaDigit,
                      },
                    )}
                  </FieldWarningText>
                )}
            </FieldRoot>
          )}
        />

        {/* areaCode。 単一 select の正は DS `Select`
            (`~/components/common/FormSelect` の JSDoc 参照)。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={'areaCode' in errors && errors.areaCode !== undefined}
          required
        >
          {/* DS `Select` は FieldRoot context の id を自動で使うため htmlFor を渡さない
              (渡すと存在しない id を指す label になる)。 */}
          <FieldLabel>
            {t('page:adminOcctoMaster.plantMaster.form.areaCode')}
            <FieldRequiredIndicator />
          </FieldLabel>
          <FormSelect
            aria-label={t('page:adminOcctoMaster.plantMaster.form.areaCode')}
            control={control}
            isDisabled={disabled}
            name="areaCode"
            numeric
            options={areaCodeOptions}
          />
          {'areaCode' in errors && errors.areaCode !== undefined && (
            <FieldErrorText>
              {
                (errors as Record<string, { message?: string }>)['areaCode']
                  ?.message
              }
            </FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/* plantName + plantNameAbbr: 2カラム (design 順: 発電所名（フル） / 略称) */}
      <SimpleGrid gap={3} columns={2}>
        {/* plantName */}
        <Controller
          control={control}
          name="plantName"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.plantName !== undefined}
              required
            >
              <FieldLabel htmlFor="plantmaster-plantName">
                {t('page:adminOcctoMaster.plantMaster.form.plantName')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <Input
                disabled={disabled}
                {...field}
                id="plantmaster-plantName"
                maxLength={100}
              />
              {errors.plantName !== undefined && (
                <FieldErrorText>{errors.plantName.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />

        {/* plantNameAbbr (schema.ts で .min(1) = required、 design HTML も
            `class="form-label required" aria-required="true"` で必須。
            従来 impl は required 表示が抜けていた。
            [DS差異] maxLength は spec の 50 を採用 (design HTML の 10 は drift、
            spec/schema が SoT)。 */}
        <Controller
          control={control}
          name="plantNameAbbr"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.plantNameAbbr !== undefined}
              required
            >
              {/* ⓘ tooltip は FieldLabel の外 (兄弟) に置く。
                  <label> の中に aria-label 付き Box を入れると accessible name が
                  「略称 補足情報」に膨らむため、 HStack で横並びに配置する
                  (contractIdNo1 と同型 pattern)。 */}
              <HStack gap={1}>
                <FieldLabel htmlFor="plantmaster-plantNameAbbr">
                  {t('page:adminOcctoMaster.plantMaster.form.plantNameAbbr')}
                  <FieldRequiredIndicator />
                </FieldLabel>
                <HelpIcon
                  ariaLabel={t(
                    'page:adminOcctoMaster.plantMaster.form.plantNameAbbrTooltipAriaLabel',
                  )}
                  content={t(
                    'page:adminOcctoMaster.plantMaster.form.plantNameAbbrTooltip',
                  )}
                />
              </HStack>
              <Input
                disabled={disabled}
                {...field}
                id="plantmaster-plantNameAbbr"
                maxLength={50}
              />
              {errors.plantNameAbbr !== undefined && (
                <FieldErrorText>{errors.plantNameAbbr.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* powerType + voltageClass: 2カラム */}
      <SimpleGrid gap={3} columns={2}>
        {/* powerType */}
        <FieldRoot
          invalid={'powerType' in errors && errors.powerType !== undefined}
          required
        >
          <FieldLabel>
            {t('page:adminOcctoMaster.plantMaster.form.powerType')}
            <FieldRequiredIndicator />
          </FieldLabel>
          <FormSelect
            aria-label={t('page:adminOcctoMaster.plantMaster.form.powerType')}
            control={control}
            isDisabled={disabled}
            name="powerType"
            numeric
            options={powerTypeOptions}
            placeholder={t(
              'page:adminOcctoMaster.plantMaster.form.powerTypeUnselected',
            )}
          />
          {'powerType' in errors && errors.powerType !== undefined && (
            <FieldErrorText>
              {
                (errors as Record<string, { message?: string }>)['powerType']
                  ?.message
              }
            </FieldErrorText>
          )}
        </FieldRoot>

        {/* voltageClass */}
        <FieldRoot
          invalid={
            'voltageClass' in errors && errors.voltageClass !== undefined
          }
          required
        >
          <FieldLabel>
            {t('page:adminOcctoMaster.plantMaster.form.voltageClass')}
            <FieldRequiredIndicator />
          </FieldLabel>
          <FormSelect
            aria-label={t(
              'page:adminOcctoMaster.plantMaster.form.voltageClass',
            )}
            control={control}
            isDisabled={disabled}
            name="voltageClass"
            numeric
            options={voltageClassOptions}
            placeholder={t(
              'page:adminOcctoMaster.plantMaster.form.voltageClassUnselected',
            )}
          />
          {'voltageClass' in errors && errors.voltageClass !== undefined && (
            <FieldErrorText>
              {
                (errors as Record<string, { message?: string }>)['voltageClass']
                  ?.message
              }
            </FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/* maxReceivingPowerKw + businessMasterId: 2カラム */}
      <SimpleGrid gap={3} columns={2}>
        {/* maxReceivingPowerKw (必須: proto required / DB NOT NULL に一致) */}
        <Controller
          control={control}
          name="maxReceivingPowerKw"
          render={({ field }) => (
            <FieldRoot
              invalid={
                'maxReceivingPowerKw' in errors &&
                errors.maxReceivingPowerKw !== undefined
              }
              required
            >
              <FieldLabel htmlFor="plantmaster-maxReceivingPowerKw">
                {t(
                  'page:adminOcctoMaster.plantMaster.form.maxReceivingPowerKw',
                )}
                <FieldRequiredIndicator />
              </FieldLabel>
              <Input
                disabled={disabled}
                {...field}
                id="plantmaster-maxReceivingPowerKw"
                inputMode="decimal"
                value={field.value ?? ''}
              />
              {'maxReceivingPowerKw' in errors &&
                errors.maxReceivingPowerKw !== undefined && (
                  <FieldErrorText>
                    {
                      (errors as Record<string, { message?: string }>)[
                        'maxReceivingPowerKw'
                      ]?.message
                    }
                  </FieldErrorText>
                )}
            </FieldRoot>
          )}
        />

        {/* businessMasterId (代表事業者 FK select)。 members[0] への同期は
            OwnerMembersSection が businessMasterId の変化を監視して行う
            (= 同期ロジックは BgMaster と共通化) ため、 ここでは副作用を持たない。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.businessMasterId !== undefined}
          required
        >
          <FieldLabel>
            {t('page:adminOcctoMaster.plantMaster.form.businessMaster')}
            <FieldRequiredIndicator />
          </FieldLabel>
          <FormSelect
            aria-label={t(
              'page:adminOcctoMaster.plantMaster.form.businessMaster',
            )}
            control={control}
            fallbackLabel={(id) => businessMasterFallbackLabel(id, targetDate)}
            isDisabled={disabled}
            name="businessMasterId"
            options={businessMasterOptions}
            placeholder={t(
              'page:adminOcctoMaster.plantMaster.form.businessMasterUnselected',
            )}
          />
          {errors.businessMasterId !== undefined && (
            <FieldErrorText>{errors.businessMasterId.message}</FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/* priority + proRataPriority: 2カラム (design 順: 優先順位 / プロラタ内優先順位) */}
      <SimpleGrid gap={3} columns={2}>
        {/* priority (任意: AC-05/AC-05b/AC-06、空欄のまま保存可) */}
        <Controller
          control={control}
          name="priority"
          render={({ field: { value, onChange } }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.priority !== undefined}
            >
              <FieldLabel htmlFor="plantmaster-priority">
                {t('page:adminOcctoMaster.plantMaster.form.priority')}
              </FieldLabel>
              <Input
                disabled={disabled}
                id="plantmaster-priority"
                inputMode="numeric"
                max={99}
                min={1}
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw === '') {
                    onChange(undefined)
                  } else {
                    const num = Number(raw)
                    if (!Number.isNaN(num)) {
                      onChange(num)
                    }
                  }
                }}
                type="number"
                value={value !== undefined ? String(value) : ''}
              />
              {errors.priority !== undefined && (
                <FieldErrorText>{errors.priority.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />

        {/* proRataPriority (任意)。 design HTML #plm-prorata の
            `<span class="form-hint">同順位が競合する場合のみ入力します。</span>` を反映。 */}
        <Controller
          control={control}
          name="proRataPriority"
          render={({ field: { value, onChange } }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={
                'proRataPriority' in errors &&
                errors.proRataPriority !== undefined
              }
            >
              <FieldLabel htmlFor="plantmaster-proRataPriority">
                {t('page:adminOcctoMaster.plantMaster.form.proRataPriority')}
              </FieldLabel>
              <Input
                disabled={disabled}
                id="plantmaster-proRataPriority"
                inputMode="numeric"
                max={99}
                min={1}
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw === '') {
                    onChange(undefined)
                  } else {
                    const num = Number(raw)
                    if (!Number.isNaN(num)) {
                      onChange(num)
                    }
                  }
                }}
                type="number"
                value={value !== undefined ? String(value) : ''}
              />
              {'proRataPriority' in errors &&
                errors.proRataPriority !== undefined && (
                  <FieldErrorText>
                    {
                      (errors as Record<string, { message?: string }>)[
                        'proRataPriority'
                      ]?.message
                    }
                  </FieldErrorText>
                )}
              <Text
                as="span"
                textStyle="captionNormal"
                marginTop={1}
                color="Text.midEmphasis"
              >
                {t(
                  'page:adminOcctoMaster.plantMaster.form.proRataPriorityHint',
                )}
              </Text>
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* contractIdNo2 (任意、design HTML #plm-contract2 は独立行)。
          design の data-tooltip「発電所(系統コード)単位の契約識別子です。」を反映。 */}
      <Controller
        control={control}
        name="contractIdNo2"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={
              'contractIdNo2' in errors && errors.contractIdNo2 !== undefined
            }
          >
            <HStack gap={1}>
              <FieldLabel htmlFor="plantmaster-contractIdNo2">
                {t('page:adminOcctoMaster.plantMaster.form.contractIdNo2')}
              </FieldLabel>
              <HelpIcon
                ariaLabel={t(
                  'page:adminOcctoMaster.plantMaster.form.contractIdNo2TooltipAriaLabel',
                )}
                content={t(
                  'page:adminOcctoMaster.plantMaster.form.contractIdNo2Tooltip',
                )}
              />
            </HStack>
            <Input
              disabled={disabled}
              {...field}
              id="plantmaster-contractIdNo2"
              value={field.value ?? ''}
            />
            {'contractIdNo2' in errors &&
              errors.contractIdNo2 !== undefined && (
                <FieldErrorText>
                  {
                    (errors as Record<string, { message?: string }>)[
                      'contractIdNo2'
                    ]?.message
                  }
                </FieldErrorText>
              )}
          </FieldRoot>
        )}
      />

      {/* note (design 順: 契約識別番号2 の直後、 valid period の前。 design HTML の
          `grid-column: 1 / -1` full-width と等価に単独行で描画する) */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel>
              {t('page:adminOcctoMaster.plantMaster.form.note')}
            </FieldLabel>
            <Textarea
              disabled={disabled}
              {...field}
              value={field.value ?? ''}
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      {/* validFrom + validTo — 共通 TopLevelValidPeriodFields */}
      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        disabled={disabled}
        idPrefix="plantmaster"
        validFromError={
          'validFrom' in errors
            ? (errors as Record<string, { message?: string }>)['validFrom']
                ?.message
            : undefined
        }
        validToError={
          'validTo' in errors
            ? (errors as Record<string, { message?: string }>)['validTo']
                ?.message
            : undefined
        }
      />

      {/* 合同所有者 編集 UI (代表同期含む。 BgMaster と共通)。
          [design差異] design HTML `#plant-edit-modal` にこのセクションは無いが、
          spec `docs/specs/admin/master/occto-master/plant.md` L32 (Owners) で
          必須リストとして定義されており、 impl 側が spec 準拠。 design HTML 側の
          drift として残置 (auditing-spec-drift の領分)。 */}
      <OwnerMembersSection
        businessMasterFallbackLabel={businessMasterFallbackLabel}
        businessMasters={businessMasters}
        control={control as unknown as Control<FieldValues>}
        disabled={disabled}
        i18nNs="adminOcctoMaster.plantMaster"
        recordValidFrom={validFrom}
        repFieldName="businessMasterId"
        setValue={setValue as unknown as UseFormSetValue<FieldValues>}
      />
    </Stack>
  )
}
